import { ColumnModel } from "../models";

export const formatColumnHeaderName = (name: string, uniqueDataKey?: string) => {
    if (name === uniqueDataKey) return "ID";
    return name.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
}

export function formatPhoneNumber(phoneNumber: string): string {
    const trimmed = phoneNumber.trim();
    const compact = trimmed.replace(/[^\d+]/g, '');
    const localNumber = compact.startsWith('+44') ? `0${compact.slice(3)}` : compact;

    if (!/^0\d{10}$/.test(localNumber)) {
        return trimmed;
    }

    const formatGroups = (groups: number[]) => {
        let cursor = 0;
        return groups
            .map((groupLength) => {
                const group = localNumber.slice(cursor, cursor + groupLength);
                cursor += groupLength;
                return group;
            })
            .join(' ');
    };

    if (localNumber.startsWith('02')) {
        return formatGroups([3, 4, 4]);
    }

    if (localNumber.startsWith('07')) {
        return formatGroups([5, 6]);
    }

    if (
        localNumber.startsWith('03')
        || localNumber.startsWith('0800')
        || localNumber.startsWith('0808')
        || localNumber.startsWith('084')
        || localNumber.startsWith('087')
        || localNumber.startsWith('09')
        || /^(011[3-8]|0121|0131|0141|0151|0161|0191)/.test(localNumber)
    ) {
        return formatGroups([4, 3, 4]);
    }

    if (localNumber.startsWith('01')) {
        return formatGroups([5, 3, 3]);
    }

    return trimmed;
}

// ===== Helper formatting =====
export const fmtGBP = (value: number) =>
value.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 3 });


export const fmtDateTime = (iso: string) => new Date(iso).toLocaleString();

// Convert a local Date to YYYY-MM-DD (input[type=date] friendly)
export const toDateInputValue = (d: Date) => {
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60 * 1000);
    return local.toISOString().slice(0, 10);
};


// Clamp start of day / end of day ISO strings
export const startOfDayIso = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.toISOString();
};
export const endOfDayIso = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.toISOString();
};

// Current month helpers
export const firstDayOfCurrentMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const todayLocal = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

type PartialDataModel<T> = {
    records: T[];
    rawData: T[];
}

export function addOrUpdateData<T>(
    data: PartialDataModel<T>,
    item: T,
    columnConfig: Record<string, Partial<ColumnModel>>,
    uniqueKey: keyof T
): PartialDataModel<T> {
    const transformedRecord = { ...item };

    // Apply formatting logic
    Object.keys(columnConfig).forEach(key => {
        const config = columnConfig[key];
        if (config?.formatter) {
            transformedRecord[key as keyof T] = config.formatter(item);
        }
    });

    // Update records
    data.records = data.records.map(record =>
        record[uniqueKey] === transformedRecord[uniqueKey] ? { ...record, ...transformedRecord } : record
    );

    // Update rawData
    data.rawData = data.rawData.map(raw =>
        raw[uniqueKey] === item[uniqueKey] ? { ...raw, ...item } : raw
    );

    // Add if not already in records or rawData
    if (!data.rawData.some(raw => raw[uniqueKey] === item[uniqueKey])) {
        data.records.push(transformedRecord);
        data.rawData.push(item);
    }

    return data;
}

type DataModel<T> = {
    recordCount: number;
    records: T[];
    rawData: T[];
    columns: ColumnModel[];
}

export function setFormattedData<T extends object>(
    { recordCount, rawData } : { recordCount?: number, rawData: T[] },
    columnConfig: Record<string, Partial<ColumnModel>>,
    uniqueDataKey: string
): DataModel<T> {
    const formattedData: {
        recordCount: number;
        records: T[];
        rawData: T[];
        columns: ColumnModel[];
    } = {
        recordCount: recordCount || rawData.length,
        records: rawData.map(record => {
            const transformedRecord = { ...record };

            // Apply formatting logic
            Object.keys(columnConfig).forEach(key => {
                const config = columnConfig[key];
                if (config?.formatter) {
                    transformedRecord[key as keyof T] = config.formatter(record);
                }
            });

            return transformedRecord;
        }),
        rawData,
        columns: [],
    };

    if (formattedData.records.length > 0) {
        // Get all keys from the first record
        const recordKeys = Object.keys(formattedData.records[0]);

        // Filter and sort keys based on columnConfig
        const filteredKeys = recordKeys
            .filter(key => columnConfig[key]) // Only include keys present in columnConfig
            .sort((a, b) =>
                Object.keys(columnConfig).indexOf(a) - Object.keys(columnConfig).indexOf(b)
            );

        // Build column properties
        filteredKeys.forEach((key, index) => {
            const column: ColumnModel = {
                align: "left",
                width: 150,            // Default properties
                ...columnConfig[key],  // Overrides from columnConfig
                header: columnConfig[key]?.header || formatColumnHeaderName(key, uniqueDataKey),
                dataKey: key,
                fixed: index === 0 || columnConfig[key]?.fixed, // First column is fixed unless overridden
                minWidth: columnConfig[key]?.minWidth || (index === filteredKeys.length - 1 ? 140 : undefined), // Prevent last column from shrinking
            };

            formattedData.columns.push(column);
        });
    }

    return formattedData;
}

export const formatDate = (isoString: string) => {
    const date = new Date(isoString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}`;
};

// Utility function to format a date according to a given pattern.
export const formatDateCustom = (date: Date | string, format: string): string => {
  // Convert the input to a Date object.
    const d = new Date(date);

    // Helper function to pad numbers with leading zeros.
    const pad = (num: number, size = 2) => num.toString().padStart(size, '0');

    // Define the token replacements.
    const replacements: { [key: string]: string } = {
        'YYYY': d.getFullYear().toString(),
        'MM': pad(d.getMonth() + 1),  // getMonth() is zero-indexed
        'DD': pad(d.getDate()),
        'hh': pad(d.getHours()),
        'mm': pad(d.getMinutes()),
        'ss': pad(d.getSeconds()),
    };

    // Replace each token in the format string with its corresponding value.
    return Object.keys(replacements).reduce(
        (acc, token) => acc.replace(new RegExp(token, 'g'), replacements[token]),
        format
    );
};

export const formatEventDisplayName = (value: string): string => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) return "";

    const followUpLabels: Record<string, string> = {
        get_context: 'Follow-up details',
        confirm_participant: 'Confirm participant',
        record_promise: 'Save promised date',
        request_staff_help: 'Request staff help',
        send_sms: 'Send text message',
    };
    if (followUpLabels[trimmed]) return followUpLabels[trimmed];

    const underscoreIndex = trimmed.indexOf('_');
    if (underscoreIndex <= 0 || underscoreIndex >= trimmed.length - 1) {
        return trimmed;
    }

    const functionName = trimmed.slice(underscoreIndex + 1);

    return functionName
        .replace(/_/g, ' ')
        .replace(/Async\b/g, '')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim() || trimmed;
};
